import {
  Avatar,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  Dialog,
  DialogClose,
  DialogContent,
  DialogTrigger,
  EmptyState,
  ErrorState,
  PageHeader,
  Skeleton,
  Table,
  TableWrap,
  Td,
  Th,
  Tr,
  toast,
} from '@stayafrica/ui';
import type { DeliveryLink } from '@stayafrica/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import {
  AlertTriangle,
  Check,
  KeyRound,
  Link2,
  Monitor,
  Pencil,
  ShieldCheck,
  ShieldOff,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Can } from '@/features/auth/session';
import { rolesQueryOptions } from '@/features/roles/queries';
import { DeliveryLinkDialog } from '@/features/users/DeliveryLinkDialog';
import { UserFormDrawer } from '@/features/users/UserFormDrawer';
import {
  resendInvitation,
  resetUserPassword,
  revokeUserSessions,
  setUserActive,
  userKeys,
  userQueryOptions,
  userSessionsQueryOptions,
} from '@/features/users/queries';
import { errorMessage, errorTraceId } from '@/lib/errors';
import { formatDateTime, formatPhone, formatRelative } from '@/lib/format';

export const Route = createFileRoute('/_app/admin/users/$userId')({
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(userQueryOptions(params.userId)),
  component: UserDetailPage,
});

function UserDetailPage() {
  const { t, i18n } = useTranslation(['users', 'common', 'errors', 'profile']);
  const { userId } = Route.useParams();
  const queryClient = useQueryClient();
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Lien d'activation ou de réinitialisation remis à l'écran (D-055).
  const [deliveryLink, setDeliveryLink] = useState<DeliveryLink | null>(null);

  const userQuery = useQuery(userQueryOptions(userId));
  const sessionsQuery = useQuery(userSessionsQueryOptions(userId));
  const rolesQuery = useQuery(rolesQueryOptions());

  const invalidate = () => queryClient.invalidateQueries({ queryKey: userKeys.all });

  const toggleActive = useMutation({
    mutationFn: (active: boolean) => setUserActive(userId, active),
    onSuccess: async (updated) => {
      await invalidate();
      toast.success(
        updated.status === 'ACTIVE' ? t('users:toasts.activated') : t('users:toasts.deactivated'),
      );
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  const resetPassword = useMutation({
    mutationFn: () => resetUserPassword(userId),
    onSuccess: (link) => {
      toast.success(
        link.emailSent ? t('users:toasts.passwordReset') : t('users:toasts.passwordResetNoEmail'),
      );
      setDeliveryLink(link);
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  const resendInvite = useMutation({
    mutationFn: () => resendInvitation(userId),
    onSuccess: (link) => {
      toast.success(
        link.emailSent
          ? t('users:toasts.invitationResent')
          : t('users:toasts.invitationResentNoEmail'),
      );
      setDeliveryLink(link);
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  const revokeSessions = useMutation({
    mutationFn: () => revokeUserSessions(userId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: userKeys.sessions(userId) });
      toast.success(t('users:toasts.sessionsRevoked'));
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  if (userQuery.isPending) {
    return (
      <Container>
        <div className="stack" aria-busy="true">
          <Skeleton variant="title" />
          <Skeleton height={180} />
          <Skeleton height={240} />
        </div>
      </Container>
    );
  }

  if (userQuery.isError || !userQuery.data) {
    return (
      <Container>
        <ErrorState
          title={t('common:states.errorTitle')}
          description={errorMessage(userQuery.error, t)}
          traceId={errorTraceId(userQuery.error)}
          retryLabel={t('common:actions.retry')}
          onRetry={() => void userQuery.refetch()}
          traceCopyLabel={t('common:actions.copy')}
          traceCopiedLabel={t('common:actions.copied')}
        />
      </Container>
    );
  }

  const user = userQuery.data;
  const sessions = sessionsQuery.data ?? [];
  const permissions = new Set(
    user.roles.flatMap(
      (assignment) =>
        rolesQuery.data?.find((role) => role.id === assignment.roleId)?.permissions ?? [],
    ),
  );

  return (
    <Container>
      <PageHeader
        title={user.fullName}
        subtitle={user.email}
        breadcrumb={[
          { label: t('users:title'), link: <Link to="/admin/users">{t('users:title')}</Link> },
          { label: user.fullName },
        ]}
        actions={
          <Can permission="user.update">
            <Button variant="secondary" onClick={() => setDrawerOpen(true)}>
              <Pencil className="ico" aria-hidden="true" />
              {t('users:actions.edit')}
            </Button>
          </Can>
        }
      />

      {user.status === 'DISABLED' ? (
        <div className="mb-4">
          <Card variant="flat">
            <CardBody density="tight">
              <div className="row">
                <ShieldOff
                  className="ico ico-20"
                  style={{ color: 'var(--danger)' }}
                  aria-hidden="true"
                />
                <span className="strong">{t('users:status.DISABLED')}</span>
                <span className="spacer" />
                <Can permission="user.deactivate">
                  <Button
                    variant="primary"
                    size="sm"
                    loading={toggleActive.isPending}
                    onClick={() => toggleActive.mutate(true)}
                  >
                    {t('users:actions.activate')}
                  </Button>
                </Can>
              </div>
            </CardBody>
          </Card>
        </div>
      ) : null}

      <div className="grid grid--main-aside">
        <div className="stack">
          <Card>
            <CardHeader
              title={t('users:detail.profile')}
              icon={<Avatar name={user.fullName} size="sm" />}
            />
            <CardBody>
              <dl className="dl dl--lines">
                <dt>{t('common:fields.email')}</dt>
                <dd>{user.email}</dd>
                <dt>{t('common:fields.phone')}</dt>
                <dd>{user.phone ? formatPhone(user.phone) : '—'}</dd>
                <dt>{t('common:fields.language')}</dt>
                <dd>{t(`common:languages.${user.locale}`)}</dd>
                <dt>{t('users:columns.status')}</dt>
                <dd>
                  <Badge
                    tone={
                      user.status === 'ACTIVE'
                        ? 'green'
                        : user.status === 'INVITED'
                          ? 'blue'
                          : 'neutral'
                    }
                    dot
                  >
                    {t(`users:status.${user.status}`)}
                  </Badge>
                </dd>
                <dt>{t('users:columns.lastLogin')}</dt>
                <dd>
                  {user.lastLoginAt
                    ? formatDateTime(user.lastLoginAt, i18n.language)
                    : t('users:never')}
                </dd>
                <dt>{t('common:fields.createdAt')}</dt>
                <dd>{formatDateTime(user.createdAt, i18n.language)}</dd>
              </dl>
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title={t('users:detail.sessionsCard')}
              icon={<Monitor className="ico ico-16" aria-hidden="true" />}
              iconTone="blue"
              actions={
                sessions.length > 0 ? (
                  <Can permission="session.revoke">
                    <Button
                      variant="danger"
                      size="sm"
                      loading={revokeSessions.isPending}
                      onClick={() => revokeSessions.mutate()}
                    >
                      {t('users:actions.revokeSessions')}
                    </Button>
                  </Can>
                ) : null
              }
            />
            {sessions.length === 0 ? (
              <CardBody>
                <p className="muted mb-0">{t('users:detail.noSessions')}</p>
              </CardBody>
            ) : (
              <TableWrap>
                <Table compact>
                  <thead>
                    <tr>
                      <Th>{t('users:detail.sessionDevice')}</Th>
                      <Th>{t('users:detail.sessionIp')}</Th>
                      <Th>{t('users:detail.sessionLastSeen')}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {sessions.map((session) => (
                      <Tr key={session.id}>
                        <Td strong>
                          {session.userAgent ?? '—'}
                          {session.current ? (
                            <Badge tone="violet" className="ml-2">
                              {t('users:detail.sessionCurrent')}
                            </Badge>
                          ) : null}
                        </Td>
                        <Td numeric className="t-13">
                          {session.ip ?? '—'}
                        </Td>
                        <Td numeric className="t-13">
                          {formatRelative(session.lastSeenAt, i18n.language)}
                        </Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrap>
            )}
          </Card>
        </div>

        <div className="stack sticky-aside">
          <Card>
            <CardHeader
              title={t('users:detail.rolesCard')}
              icon={<ShieldCheck className="ico ico-16" aria-hidden="true" />}
            />
            <CardBody>
              <div className="row row--wrap" style={{ gap: 6 }}>
                {user.roles.map((role) => (
                  <Badge key={role.roleId} tone="violet" size="lg">
                    {role.roleName}
                  </Badge>
                ))}
              </div>
              <p className="hint mt-3 mb-0">
                {t('users:detail.permissionsCount', { count: permissions.size })}
              </p>
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title={t('users:detail.securityCard')}
              icon={<KeyRound className="ico ico-16" aria-hidden="true" />}
              iconTone="amber"
            />
            <CardBody>
              <div className="stack stack--md">
                <div className="row">
                  {user.mfaEnabled ? (
                    <Badge tone="green">
                      <Check className="ico ico-14" aria-hidden="true" />
                      {t('users:mfaEnabled')}
                    </Badge>
                  ) : (
                    <Badge tone="amber">
                      <AlertTriangle className="ico ico-14" aria-hidden="true" />
                      {t('users:mfaDisabled')}
                    </Badge>
                  )}
                </div>

                {user.status === 'INVITED' ? (
                  <Can permission="user.create">
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button variant="secondary" block>
                          <Link2 className="ico" aria-hidden="true" />
                          {t('users:actions.resendInvitation')}
                        </Button>
                      </DialogTrigger>
                      <DialogContent
                        title={t('users:confirm.resendTitle', { name: user.fullName })}
                        description={t('users:confirm.resendBody')}
                        closeLabel={t('common:actions.close')}
                        icon={<Link2 className="ico ico-16" aria-hidden="true" />}
                        iconTone="blue"
                        footer={
                          <>
                            <DialogClose asChild>
                              <Button variant="ghost">{t('common:actions.cancel')}</Button>
                            </DialogClose>
                            <DialogClose asChild>
                              <Button
                                variant="primary"
                                loading={resendInvite.isPending}
                                onClick={() => resendInvite.mutate()}
                              >
                                {t('users:confirm.resendConfirm')}
                              </Button>
                            </DialogClose>
                          </>
                        }
                      />
                    </Dialog>
                  </Can>
                ) : null}

                <Can permission="user.reset_password">
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="secondary" block>
                        {t('users:actions.resetPassword')}
                      </Button>
                    </DialogTrigger>
                    <DialogContent
                      title={t('users:confirm.resetTitle', { name: user.fullName })}
                      description={t('users:confirm.resetBody')}
                      closeLabel={t('common:actions.close')}
                      icon={<KeyRound className="ico ico-16" aria-hidden="true" />}
                      iconTone="amber"
                      footer={
                        <>
                          <DialogClose asChild>
                            <Button variant="ghost">{t('common:actions.cancel')}</Button>
                          </DialogClose>
                          <DialogClose asChild>
                            <Button
                              variant="primary"
                              loading={resetPassword.isPending}
                              onClick={() => resetPassword.mutate()}
                            >
                              {t('users:confirm.resetConfirm')}
                            </Button>
                          </DialogClose>
                        </>
                      }
                    />
                  </Dialog>
                </Can>

                {user.status === 'ACTIVE' ? (
                  <Can permission="user.deactivate">
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button variant="danger" block>
                          {t('users:actions.deactivate')}
                        </Button>
                      </DialogTrigger>
                      <DialogContent
                        title={t('users:confirm.deactivateTitle', { name: user.fullName })}
                        description={t('users:confirm.deactivateBody')}
                        closeLabel={t('common:actions.close')}
                        icon={<ShieldOff className="ico ico-16" aria-hidden="true" />}
                        iconTone="red"
                        footer={
                          <>
                            <DialogClose asChild>
                              <Button variant="ghost">{t('common:actions.cancel')}</Button>
                            </DialogClose>
                            <DialogClose asChild>
                              <Button
                                variant="danger"
                                loading={toggleActive.isPending}
                                onClick={() => toggleActive.mutate(false)}
                              >
                                {t('users:confirm.deactivateConfirm')}
                              </Button>
                            </DialogClose>
                          </>
                        }
                      />
                    </Dialog>
                  </Can>
                ) : null}
              </div>
            </CardBody>
          </Card>

          {sessionsQuery.isError ? (
            <EmptyState
              title={t('common:states.errorTitle')}
              description={errorMessage(sessionsQuery.error, t)}
            />
          ) : null}
        </div>
      </div>

      <UserFormDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        roles={rolesQuery.data ?? []}
        user={user}
      />

      <DeliveryLinkDialog
        link={deliveryLink}
        recipient={{ fullName: user.fullName, email: user.email }}
        onClose={() => setDeliveryLink(null)}
      />
    </Container>
  );
}
